import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router';
import { HttpHeaders } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { ConfigService } from '@ngx-config/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { isArray, isObject, isNullOrUndefined, isString, isNumber } from 'util';
import { sprintf } from 'sprintf-js';
import { cloneDeep, has, uniq, sortBy } from 'lodash';
import { OAuthService } from 'angular-oauth2-oidc';

import * as moment from 'moment';
import 'moment-timezone';

import * as xenaSelectors from '../../../state-management/reducers/index';
import * as appSelectors from '../../../../../shared/state-management/reducers/index';
import * as topsFlopsSelectors from '../../components/tops-flops/state-management/reducers/index';
import * as SearchformActions from '../../../state-management/actions/searchform';

import { Group } from '../../models/groups/groups.model';
import { GroupsResponseModel } from '../../models/groups/groups-response.model';
import { createQueryFromSearchformNfModel, createQueryFromSearchformNhModel, createQueryFromSearchformPaModel, InitialQueryState, Query, QueryModel } from '../../models/query/query.model';
import { NewWebserviceRegionsResponse, RegionsResponseModel } from '../../models/regions-response.model';
import { ValidationResponseModel } from '../../models/validation-response.model';
import { CrsXMLResponseModel } from '../../models/crx-xml-response.model';
import { SearchformResponseModel, TourOperator } from '../../models/searchform-response.model';
import { getXenaConfig, getXenaGlobalConfig } from '../../../../../shared/state-management/reducers';
import { TourOperatorModel } from '../../models/tour-operator.model';
import { searchType as getSearchType } from '../../../state-management/reducers/index';
import { TourOperatorService } from '../tour-operator/tour-operator.service';
import { TourOperatorsActivationState } from '@gosuite/xena-admin';
import { mapDeprecatedTourOperatorActivationModel } from '../../../../../shared/services/config/helper/xena-admin-tour-operator-config-helper';
import { Criteria, criteriaToPostParams } from '../../models/criteria';
import { ApiKeysResponse } from '../../../../../shared/services/api-keys/api-keys.model';
import { Agency } from '../../../../../shared/services/agencies/agencies.model';
import { MultiRoom } from '../../../searchform/multi-room-dialog/multi-room-utils';
import { MULTI_ROOM_UNSUITABLE_TOUR_OPERATORS } from '../../../../../shared/help/multi-room';
import { SearchMask } from '../../models/searchmask.model';
import { CommissionSetting } from '../../../../../shared/services/config/models/xena-global-config';



@Injectable()
export class WebserviceService implements Resolve<SearchformResponseModel> {
	private _neoXmlSubject: Subject<string> = new Subject();
	private _searchformCache: SearchformResponseModel;
	private _selectedLanguage: string = '';

	private _validationStatus$ = this._store.select(getXenaConfig).map(config => {
		if (
			isObject(config)
			&& has(config, 'validationSettings.status')
			&& isString(config.validationSettings.status)
			&& ['OK', 'XX'].indexOf(config.validationSettings.status) !== -1
		) {
			return config.validationSettings.status;
		}

		return null;
	});

	private commissionSettings$ = this._store.select(getXenaGlobalConfig)
		.map(globalConfig => globalConfig.commissionSettings)
		.map(commissionSettings =>
			sortBy(commissionSettings, ['tourop', 'journey_type', 'booking_valid_from', 'booking_valid_till', 'traveled_valid_from', 'traveled_valid_till', 'giata_id'])
		);

	public baseUrl: string = '';
	public neoXml$: Observable<string> = this._neoXmlSubject.asObservable();

	public tourOperatorCodes$: Observable<Array<string>> = Observable.combineLatest(
		this._store.select(getSearchType),
		this._store.select(getXenaGlobalConfig),
		this._store.select(getXenaConfig),
		(type, globalConfig, config) => {
			const searchType = isString(type) && type.length ? type : InitialQueryState.type;

			const tourOperators: Array<TourOperatorModel> = isObject(globalConfig) && isObject(globalConfig.touroperator)
				? Object.keys(globalConfig.touroperator).map(key => globalConfig.touroperator[key])
				: null;

			let adminTourOperatorConfig: TourOperatorsActivationState = null;

			if (isObject(config) && isObject(config.tourOperatorSettings)) {
				if (isObject(config.tourOperatorSettings.activation)) {
					adminTourOperatorConfig = config.tourOperatorSettings.activation;
				} else if (isArray(config.tourOperatorSettings.activeState)) {
					adminTourOperatorConfig = mapDeprecatedTourOperatorActivationModel(config.tourOperatorSettings.activeState);
				}
			}

			return isArray(tourOperators) && !!(tourOperators.length)
				? this._tourOperatorService.filterTourOperators([searchType], tourOperators, adminTourOperatorConfig).map(top => 'TOP-' + top.rawValue)
				: [];
		}
	);

	public constructor(
		private _store: Store<xenaSelectors.State|appSelectors.State>,
		private _http: HttpClient,
		private _config: ConfigService,
		private _oauthService: OAuthService,
		private _tourOperatorService: TourOperatorService
	) {
		this._store.select(appSelectors.getSelectedLanguage).subscribe(lang => this._selectedLanguage = lang);
		this.baseUrl = this._config.getSettings('xena').webservice.endpoint;
	}

	public searchform(): Observable<SearchformResponseModel> {
		if (this._searchformCache) {
			return Observable.of(this._searchformCache);
		}

		return this._query<SearchformResponseModel>(`${this.baseUrl}/searchform`)
			.map(res => {
				res.tour_operator = Object.keys(res.tour_operator).map(key => <TourOperator>res.tour_operator[key]);
				return res;
			})
			.do(res => {
				if (isObject(res) && isArray(res.tour_operator)) {
					this._searchformCache = res;
					this._store.dispatch(new SearchformActions.SetSearchformSuccess(res));
				}
			});
	}

	public setXML(xml: string): void {
		this._neoXmlSubject.next(xml);
	}

	public searchRegionsNF(queryModel: QueryModel): Observable<RegionsResponseModel> {
		const topsFlopsResult: Array<string> = this._searchTopsFlops(queryModel.topsFlops);

		if (isArray(queryModel.topsFlops) && queryModel.topsFlops.length && topsFlopsResult.length === 0) {
			return Observable.of({data: []});
		} else {
			const query: Query = new Query({ ...queryModel });

			if (topsFlopsResult.length) {
				const tags: Array<string> = isString(query.tags) && query.tags.length ? query.tags.split(',') : [];
				tags.push(topsFlopsResult.map(id => `BT01-${id}`).join(':'));
				query.tags = tags.join(',');
			}

			if (!isString(query.tags) || query.tags.indexOf('TOP-') === -1) {
				let availableTourOperators: Array<string> = [];

				this.tourOperatorCodes$.take(1).subscribe(codes => availableTourOperators = codes);
				this._validationStatus$.take(1).filter(status => isString(status)).subscribe(status => query.status = status);

				if (isArray(availableTourOperators) && availableTourOperators.length) {
					if (isString(query.multiRoomOccupancy) && query.multiRoomOccupancy.length > 0) {
						availableTourOperators = availableTourOperators.filter(top => MULTI_ROOM_UNSUITABLE_TOUR_OPERATORS.indexOf(top) === -1);
					}

					const queryTags: Array<string> = isString(query.tags) && !!(query.tags.length) ? query.tags.split(',') : [];
					queryTags.push(availableTourOperators.join(':'));
					query.tags = queryTags.join(',');
				}
			}

			return this._post<RegionsResponseModel>(`${this.baseUrl}/regions`, query.toPostParams())
				.map(regions => {
					if (isObject(regions) && isArray(regions.data) && regions.data.length) {
						regions.data = regions.data.map(region => {
							region.translation = region.translation.toString() === '-1' ? region.code : region.translation;
							return region;
						});
					}

					return regions;
				});
		}
	}

	public searchRegionsPA(queryModel: QueryModel): Observable<RegionsResponseModel> {
		const topsFlopsResult: Array<string> = this._searchTopsFlops(queryModel.topsFlops);

		if (isArray(queryModel.topsFlops) && queryModel.topsFlops.length && topsFlopsResult.length === 0) {
			return Observable.of({
				data: [],
				hints: ['no matching tops-flops-hotels found']
			});
		}

		let enrichedTags: Array<string> = !!(topsFlopsResult.length) ? [ topsFlopsResult.map(id => `BT01-${id}`).join(':') ] : [];
		const tags: string = isString(queryModel.tags) && !!(queryModel.tags.length) ? queryModel.tags : null;

		if (tags) {
			enrichedTags = enrichedTags.concat(tags.split(','));

			if (tags.indexOf('TOP-') === -1) {
				let availableTourOperators: Array<string> = [];

				this.tourOperatorCodes$.take(1).subscribe(codes => availableTourOperators = codes);

				if (isArray(availableTourOperators) && availableTourOperators.length) {
					if (isString(queryModel.multiRoomOccupancy) && queryModel.multiRoomOccupancy.length > 0) {
						availableTourOperators = availableTourOperators.filter(top => MULTI_ROOM_UNSUITABLE_TOUR_OPERATORS.indexOf(top) === -1);
					}

					enrichedTags.push(availableTourOperators.join(':'));
				}
			}
		}

		const criteria: Criteria = {
			id: isString(queryModel.id) && !!(queryModel.id.length) ? queryModel.id : null,
			cfg: this._getCfg(),
			start_date: queryModel.startDate,
			end_date: queryModel.endDate,
			order_by: queryModel.orderBy,
			order_direction: <'asc'|'desc'>queryModel.orderDirection,
			limit: queryModel.limit,
			offset: queryModel.offset,
			adult_count: queryModel.adultsNumber,
			max_price: isNumber(queryModel.maxPrice) && queryModel.maxPrice > 0 ? queryModel.maxPrice : null,
			children_ages: isString(queryModel.childrenAges) && !!(queryModel.childrenAges.length)
				? queryModel.childrenAges.split(',').map(age => parseInt(age, 10))
				: null,
			tags: isArray(enrichedTags) && !!(enrichedTags.length) ? enrichedTags : null,
			currency: 'EUR',
			hotel_name: isString(queryModel.hotelName) && !!(queryModel.hotelName) ? queryModel.hotelName : null,
			service_code: isString(queryModel.bookingCode) && !!(queryModel.bookingCode) ? queryModel.bookingCode : null,
			multi_room_occupancy: isString(queryModel.multiRoomOccupancy) && !!(queryModel.multiRoomOccupancy) ? queryModel.multiRoomOccupancy : null,
		};

		this._validationStatus$.take(1).filter(status => isString(status)).subscribe(status => criteria.status = status);

		const url: string = `https://api.go-suite.com/xena/v2/${queryModel.type.toUpperCase()}/regions`;
		const body: object = criteriaToPostParams(criteria);

		return Observable.forkJoin(
			this._store.select(appSelectors.getApiKeys).take(1),
			this._store.select(appSelectors.getXenaConfigRev).take(1),
			this._store.select(appSelectors.getSelectedAgency).take(1),
		).switchMap(response => {
			const apiKeys: ApiKeysResponse = isObject(response[0]) ? response[0] : null;
			const configRev: string = isString(response[1]) && !!(response[1].length) ? response[1] : null;
			const agency: Agency = isObject(response[2]) ? response[2] : null;

			if (apiKeys && configRev && agency) {
				const apiKey: string = apiKeys.xena[agency.agentId];

				const headers: HttpHeaders = new HttpHeaders({
					'X-Api-Key': apiKey,
					'X-Api-Config-Revision': configRev
				});

				return this._http.post(url, body, { headers: headers }).map((res: NewWebserviceRegionsResponse) => ({
					data: isObject(res) && res.status === 200 ? res.data : [],
					hints: []
				}));
			}

			return Observable.of({
				data: [],
				hints: []
			});
		});
	}

	public regions(queryModel: QueryModel): Observable<RegionsResponseModel> {
		if (!isObject(queryModel)) {
			return Observable.of(null);
		}

		switch (queryModel.type) {
			case 'NF':
				return this.searchRegionsNF(queryModel);

			// PA, NH
			default:
				return this.searchRegionsPA(queryModel);
		}
	}

	public groups(selectedRegions?: Array<string>): Observable<GroupsResponseModel> {
		let query: Query = new Query();

		this._store.select(xenaSelectors.selectedQuery).take(1).subscribe(q => query = new Query({ ...q }));

		const topsFlopsResult: Array<string> = this._searchTopsFlops(query.topsFlops);

		if (isArray(query.topsFlops) && query.topsFlops.length) {
			if (topsFlopsResult.length === 0) {
				return Observable.of({
					count: 0,
					data: [],
					dayPriceMatrix: null
				});
			}

			query.tags = query.tags.split(',').concat(
				topsFlopsResult.map(hotelId => `GT01-${hotelId}:BT01-${hotelId}`).join(':')
			).join(',');
		}

		if (isArray(selectedRegions) && selectedRegions.length) {
			const tags: Array<string> = [];

			query.tags.split(',').forEach(item => {
				const filteredTags: Array<string> = item.split(':').filter(tag => tag.indexOf('XT10-') === -1 && tag.indexOf('BT10-') === -1);

				if (filteredTags.length > 0) {
					tags.push(filteredTags.join(':'));
				}
			});

			tags.push(selectedRegions.join(':'));

			query.tags = tags.join(',');
		}

		if (query.tags.indexOf('TOP-') === -1) {
			let availableTourOperators: Array<string> = [];

			this.tourOperatorCodes$.take(1).subscribe(codes => availableTourOperators = codes);

			if (isArray(availableTourOperators) && availableTourOperators.length) {
				if (isString(query.multiRoomOccupancy) && query.multiRoomOccupancy.length > 0) {
					availableTourOperators = availableTourOperators.filter(top => MULTI_ROOM_UNSUITABLE_TOUR_OPERATORS.indexOf(top) === -1);
				}

				query.tags = query.tags.split(',').concat(availableTourOperators.join(':')).join(',');
			}
		}

		this._validationStatus$.take(1).filter(status => isString(status)).subscribe(status => query.status = status);

		return this._post<GroupsResponseModel>(`${this.baseUrl}/groups`, query.toPostParams())
			.map((res: GroupsResponseModel) => WebserviceService._groupsStringDateToDate(res))
			.map((res: GroupsResponseModel) => WebserviceService._groupsOperatorsObjToArray(res))
			.map((res: GroupsResponseModel) => WebserviceService._transformTagsToMap(res))
			.map((res: GroupsResponseModel) => WebserviceService._extractImportantAttributes(res));
	}

	public groupsDataExport(mask: SearchMask, perWeek: boolean = true): Array<Observable<GroupsResponseModel>> {
		if (!perWeek) {
			return [this._createRequestBySearchMask(mask)];
		}

		const form = cloneDeep(mask.form[mask.searchType]);

		let firstMonday: moment.Moment = null;
		let mondayOffset = 0;
		let lastSunday: moment.Moment = null;
		let sundayOffset = 0;

		while (firstMonday === null || lastSunday === null) {
			const monday = moment(form.dates.fromDate).add(mondayOffset, 'days');
			const sunday = moment(form.dates.toDate).subtract(sundayOffset, 'days');

			if (monday.weekday() === 0) {
				firstMonday = monday;
			} else {
				mondayOffset++;
			}

			if (sunday.weekday() === 6) {
				lastSunday = sunday;
			} else {
				sundayOffset--;
			}
		}

		const possibleRepeats: Array<Observable<GroupsResponseModel>> = [];
		const maxDurationPerWeek = mask.form[mask.searchType].maxDuration + 6;
		const requestMask = cloneDeep(mask);

		requestMask.form[mask.searchType].minDuration = requestMask.form[mask.searchType].maxDuration;

		for (let doneWeek = 0; doneWeek < lastSunday.diff(firstMonday, 'weeks'); doneWeek++) {
			const startDate = cloneDeep(firstMonday).add(doneWeek, 'weeks');
			const endDate = cloneDeep(startDate).add(maxDurationPerWeek, 'days');

			requestMask.form[mask.searchType].dates.fromDate = cloneDeep(startDate.toDate());
			requestMask.form[mask.searchType].dates.toDate = cloneDeep(endDate.toDate());

			possibleRepeats.push(this._createRequestBySearchMask(cloneDeep(requestMask)));
		}

		return possibleRepeats;
	}

	public offers(selectedHotelId: string): Observable<GroupsResponseModel> {
		if (isString(selectedHotelId) && selectedHotelId.length) {
			let query: Query = new Query();

			this._store.select(xenaSelectors.selectedQuery).take(1).subscribe(q => query = new Query({ ...q }));

			const tags: Array<string> = [];

			query.tags.split(',').forEach(item => {
				const filteredTags: Array<string> = item.split(':')
					.filter(tag => {
						return tag.indexOf('BT10-') === -1
							&& tag.indexOf('GT10-') === -1
							&& tag.indexOf('XT10-') === -1
							&& tag.indexOf('BT09-') === -1
							&& tag.indexOf('GT09-') === -1
							&& tag.indexOf('BT01-') === -1
							&& tag.indexOf('GT01-') === -1;
					});

				if (filteredTags.length) {
					tags.push(filteredTags.join(':'));
				}
			});

			const isTourOperatorFilterActive: boolean = tags.some(tag => tag.indexOf('TOP-') !== -1);

			if (!isTourOperatorFilterActive) {
				let availableTourOperators: Array<string> = [];

				this.tourOperatorCodes$.take(1).subscribe(codes => availableTourOperators = codes);

				if (isArray(availableTourOperators) && availableTourOperators.length) {
					if (isString(query.multiRoomOccupancy) && query.multiRoomOccupancy.length > 0) {
						availableTourOperators = availableTourOperators.filter(top => MULTI_ROOM_UNSUITABLE_TOUR_OPERATORS.indexOf(top) === -1);
					}

					tags.push(availableTourOperators.join(':'));
				}
			}

			tags.push('BT01-' + selectedHotelId);

			query.tags = tags.join(',');
			query.geoJson = null;
			query.hotelName = null;
			query.maxPrice = null;
			query.topsFlops = null;

			this._validationStatus$.take(1).filter(status => isString(status)).subscribe(status => query.status = status);

			return this._post<GroupsResponseModel>(`${this.baseUrl}/termins`, query.toPostParams())
				.map((res: GroupsResponseModel) => WebserviceService._groupsStringDateToDate(res))
				.map((res: GroupsResponseModel) => WebserviceService._updateTransportFlightInfo(res))
				.map((res: GroupsResponseModel) => WebserviceService._groupsOperatorsObjToArray(res))
				.map((res: GroupsResponseModel) => WebserviceService._mapTagsToHotelAttributes(res))
				.map((res: GroupsResponseModel) => WebserviceService._setCountry(res))
				.map((res: GroupsResponseModel) => this._addCommissionInfos(res));
		}

		return Observable.of({
			count: 0,
			data: [],
			dayPriceMatrix: null
		});
	}

	public validate(offer: Group, childrenAges?: string): Observable<ValidationResponseModel> {
		if (isObject(offer)) {
			let formData: FormData = new FormData();

			this._store.select(xenaSelectors.selectedQuery).take(1).subscribe(query => {
				formData = WebserviceService._createValidateRequestFormData(
					offer.id,
					offer.tourOperator,
					query,
					childrenAges
				);
			});

			return this._post<ValidationResponseModel>(`${this.baseUrl}/validate`, formData);
		}

		return Observable.of(null);
	}

	public crs(token: string, crs: string = 'NEO', searchMaskUrl?: string, searchMaskId?: number): Observable<CrsXMLResponseModel> {
		return this._query<CrsXMLResponseModel>(`${this.baseUrl}/crs/${token}` + (crs === 'PAX' ? '/paxconnect?url=' + searchMaskUrl + '&tripId=' + searchMaskId : ''))
			.do((res: CrsXMLResponseModel) => {
				if (crs === 'NEO') {
					this.sendToNeo(res.crs);
				}
			});
	}

	public sendToNeo(crs: string): void {
		this._neoXmlSubject.next(crs);
	}

	public resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<SearchformResponseModel> {
		if (this._searchformCache) {
			return Observable.of(this._searchformCache);
		}

		return this.searchform();
	}

	private _getCfg(): string {
		const claims = this._oauthService.getIdentityClaims();
		let selectedAgency: number = -1;

		this._store.select(appSelectors.getSelectedAgency).take(1).subscribe(agency => selectedAgency = agency.agentId).unsubscribe();

		return sprintf('%06d%010d', selectedAgency, claims['sub']);
	}

	private _post<T>(url: string, body: FormData): Observable<T> {
		return Observable.forkJoin(
			this._store.select(appSelectors.getApiKeys).take(1),
			this._store.select(appSelectors.getXenaConfigRev).take(1),
			this._store.select(appSelectors.getSelectedAgency).take(1),
		).switchMap(response => {
			const apiKeys: ApiKeysResponse = isObject(response[0]) ? response[0] : null;
			const configRev: string = isString(response[1]) && !!(response[1].length) ? response[1] : null;
			const agency: Agency = isObject(response[2]) ? response[2] : null;

			if (apiKeys && configRev && agency) {
				url = sprintf(`${url}${url.indexOf('?') === -1 ? '?' : '&'}lang=${this._selectedLanguage}`, {cfg: this._getCfg()});

				const apiKey: string = apiKeys.xena[agency.agentId];

				const headers: HttpHeaders = new HttpHeaders({
					'X-Api-Key': apiKey,
					'X-Api-Config-Revision': configRev
				});

				return this._http.post<T>(url, body, { observe: 'response', headers: headers }).map((r: HttpResponse<T>) => {
					if (!r.ok) {
						throw new Error(r.body.toString());
					}

					if (r.status === 204) {
						throw new Error('No results');
					}

					return r.body;
				});
			}

			throw new Error('No results');
		});
	}

	private _query<T>(url: string): Observable<T> {
		return Observable.forkJoin(
			this._store.select(appSelectors.getApiKeys).take(1),
			this._store.select(appSelectors.getXenaConfigRev).take(1),
			this._store.select(appSelectors.getSelectedAgency).take(1),
		).switchMap((keyRev) => {
			let apiKey: string = '';
			if (isObject(keyRev[0]) && isObject(keyRev[0].xena) && isString(keyRev[0].xena[keyRev[2].agentId])) {
				apiKey = keyRev[0].xena[keyRev[2].agentId];
			}

			return this._http.get<T>(
				sprintf(
					`${url}${url.indexOf('?') === -1 ? '?' : '&'}lang=${this._selectedLanguage}`,
					{cfg: this._getCfg()}
				),
				{
					observe: 'response',
					headers: new HttpHeaders({
						'X-Api-Key': apiKey,
						'X-Api-Config-Revision': keyRev[1] || ''
					}),
				},
			)
				.map((response: HttpResponse<T>) => {
					if (!response.ok) {
						throw new Error(response.body.toString());
					}

					if (response.status === 204) {
						throw new Error('No results');
					}

					return response.body;
				});
		});
	}

	private _searchTopsFlops(topsFlops: Array<string>): Array<string> {
		let hotelIds: Array<string> = [];

		if (isArray(topsFlops) && topsFlops.length) {
			this._store.select(topsFlopsSelectors.getTopsFlops).take(1).subscribe(tfs => {
				hotelIds = uniq(Object.keys(tfs).filter(bt01 => {
					return !!tfs[bt01].attributes.find(key => topsFlops.indexOf(key) !== -1);
				}));
			});
		}

		return hotelIds;
	}

	private _addCommissionInfos(groups: GroupsResponseModel): GroupsResponseModel {
		let commissionInfos: CommissionSetting[] = [];
		this.commissionSettings$.take(1).subscribe(ci => commissionInfos = ci);
		const today = (new Date()).toISOString().substr(0, 10);

		for (const ci of commissionInfos) {
			groups.data = groups.data.map(group => {
				const startDate = typeof group.startDate === 'string' ? group.startDate : group.startDate.toISOString().substr(0, 10);
				const endDate = typeof  group.endDate === 'string' ? group.endDate : group.endDate.toISOString().substr(0, 10);

				if (
					(group.tourOperator === ci.tourop)
					&& (group.kindOfTravel === ci.journey_type || ci.journey_type === '')
					&& (String(group.hotel.locationId) === ci.giata_id || ci.giata_id === '')
					&& (ci.traveled_valid_from  && startDate >= ci.traveled_valid_from || ci.traveled_valid_from === '')
					&& (ci.traveled_valid_till  && endDate <= ci.traveled_valid_till || ci.traveled_valid_till === '')
					&& (ci.booking_valid_from  && today >= ci.booking_valid_from || ci.booking_valid_from === '')
					&& (ci.booking_valid_till  && today <= ci.booking_valid_till || ci.booking_valid_till === '')
				) {
					group.commissionInfo = ci;
				}

				return group;
			});
		}

		return groups;
	}

	private static _groupsStringDateToDate(groups: GroupsResponseModel): GroupsResponseModel {
		groups.data = groups.data.map(g => {
			if (moment(g.startDate, ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm']).isValid()) {
				g.startDate = moment(g.startDate, ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm']).toDate();
			}

			if (moment(g.endDate, ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm']).isValid()) {
				g.endDate = moment(g.endDate, ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm']).toDate();
			}

			return g;
		});

		return groups;
	}

	private static _updateTransportFlightInfo(groups: GroupsResponseModel): GroupsResponseModel {
		groups.data = groups.data.map(g => {
			g.transports.outbound.departureTime = (g.transports.outbound.departureDate || '00:00').substr(-5);
			g.transports.outbound.arrivalTime = (g.transports.outbound.arrivalDate || '00:00').substr(-5);
			g.transports.outbound.flight = `${(g.transports.outbound.carrier || 'XX')} ${(g.transports.outbound.flightNumber || '0000')}`;

			g.transports.inbound.departureTime = (g.transports.inbound.departureDate || '00:00').substr(-5);
			g.transports.inbound.arrivalTime = (g.transports.inbound.arrivalDate || '00:00').substr(-5);
			g.transports.inbound.flight = `${(g.transports.inbound.carrier || 'XX')} ${(g.transports.inbound.flightNumber || '0000')}`;

			g.transports.outbound.flightInfo = `${g.transports.outbound.departureTime} - ${g.transports.outbound.arrivalTime} | ${g.transports.outbound.flight}`;
			g.transports.inbound.flightInfo = `${g.transports.inbound.departureTime} - ${g.transports.inbound.arrivalTime} | ${g.transports.inbound.flight}`;
			return g;
		});

		return groups;
	}

	private static _groupsOperatorsObjToArray(groups: GroupsResponseModel): GroupsResponseModel {
		groups.data = groups.data.map(group => {
			const operators: Array<string> = [];

			for (const tourOperatorCode in group.alternateTourOperators) {
				if (!isNullOrUndefined(group.alternateTourOperators[tourOperatorCode])) {
					operators.push(group.alternateTourOperators[tourOperatorCode]);
				}
			}

			group.alternateTourOperators = operators;
			group.alternateTourOperatorsCount = operators.length;

			return group;
		});

		return groups;
	}

	private static _mapTagsToHotelAttributes(groups: GroupsResponseModel): GroupsResponseModel {
		groups.data = groups.data.map(g => {
			if (has(g, 'hotel.board.code') && has(g, 'multiRoomBoards')) {
				g.hotel.board.code = g.multiRoomBoards.join('\n');
			} else if (isObject(g.hotel) && g.hotel.board && g.hotel.tags && g.hotel.tags.GT06) {
				g.hotel.board.code = g.hotel.tags.GT06 && g.hotel.tags.GT06[0] && (`GT06-${g.hotel.tags.GT06[0]}`) || '';
			}

			return g;
		});

		return groups;
	}

	private static _transformTagsToMap(groups: GroupsResponseModel): GroupsResponseModel {
		groups.data = groups.data.map(g => {
			if (isObject(g.hotel) && g.hotel.tags && g.hotel.tags.GT03) {
				g.hotel.attributesMap = {};

				(g.hotel.tags.GT03 || []).forEach(gt03Tag => {
					if (gt03Tag.indexOf('/') !== -1) {
						g.hotel.attributesMap[gt03Tag.split('/')[0]] = true;
					} else {
						g.hotel.attributesMap[gt03Tag] = true;
					}
				});
			}

			return g;
		});

		return groups;
	}

	private static _extractImportantAttributes(groups: GroupsResponseModel): GroupsResponseModel {
		groups.data = groups.data.map(g => {

			g.hotel.importantAttributes = {
				beach: (g.hotel.attributesMap || {})['BEAC'],
				family: (g.hotel.attributesMap || {})['FAMI'],
				pool: (g.hotel.attributesMap || {})['POOL'],
				wellness: WebserviceService._hasWellnessTag(g),
				sport: WebserviceService._hasSportTag(g),
			};

			return g;
		});

		return groups;
	}

	private static _setCountry(groups: GroupsResponseModel): GroupsResponseModel {
		groups.data.map((group: Group) => {
			if (group.tourOperator.split('.').length > 1) {
				group.country = group.tourOperator.split('.')[1];
			}

			return group;
		});

		return groups;
	}

	private static _hasWellnessTag(group: Group): boolean {
		for (const tag of ['MASS', 'SAUN', 'SOLA', 'SPAA']) {
			if ((group.hotel.attributesMap || {})[tag]) {
				return true;
			}
		}

		return false;
	}

	private static _hasSportTag(group: Group): boolean {
		for (const tag of ['ARCH', 'BABO', 'BADM', 'BASK', 'BESP', 'BEVO', 'BILL', 'BOAL', 'BOCC', 'CANO', 'CATA', 'DART', 'GOLF', 'MINI', 'PEBO', 'SAIL', 'SQUA', 'SURF', 'TATE', 'TENN', 'WATE', 'WIND']) {
			if ((group.hotel.attributesMap || {})[tag]) {
				return true;
			}
		}

		return false;
	}

	private static _multiRoomToAgeString(multiRoom: MultiRoom): string {
		const ages = [];

		for (let i = 0; i < multiRoom.adultsNumber; i++) {
			ages.push('25');
		}

		['childAge1', 'childAge2', 'childAge3', 'childAge4'].forEach(key => {
			if (isNumber(multiRoom[key]) && multiRoom[key] >= 0 && multiRoom[key] <= 17) {
				ages.push(`${multiRoom[key]}`);
			}
		});

		return ages.join(',');
	}

	private static _createValidateRequestFormData(offerId: string, tourOperator: string, query: QueryModel, childrenAges: string): FormData {
		const formData: FormData = new FormData();

		formData.set('id', offerId);
		formData.set('type', query.type);
		formData.set('tags', 'TOP-' + tourOperator);

		if (isObject(query.occupancy)) {
			if (this._multiRoomToAgeString(query.occupancy.occupancy1).length > 0) {
				formData.set('rooms[0]', this._multiRoomToAgeString(query.occupancy.occupancy1));
			}

			if (this._multiRoomToAgeString(query.occupancy.occupancy2).length > 0) {
				formData.set('rooms[1]', this._multiRoomToAgeString(query.occupancy.occupancy2));
			}

			if (this._multiRoomToAgeString(query.occupancy.occupancy3).length > 0) {
				formData.set('rooms[2]', this._multiRoomToAgeString(query.occupancy.occupancy3));
			}

			if (this._multiRoomToAgeString(query.occupancy.occupancy4).length > 0) {
				formData.set('rooms[3]', this._multiRoomToAgeString(query.occupancy.occupancy4));
			}
		} else {
			formData.set('adult_count', query.adultsNumber.toString());

			if (isString(childrenAges) && childrenAges.length) {
				formData.set('children_ages', childrenAges);
			} else if (isString(query.childrenAges) && query.childrenAges.length) {
				formData.set('children_ages', query.childrenAges);
			}
		}

		return formData;
	}

	private _createRequestBySearchMask(mask: SearchMask): Observable<GroupsResponseModel> {
		let query: Query = new Query();

		switch (mask.searchType) {
			case 'PA':
				query = createQueryFromSearchformPaModel(mask.form[mask.searchType]);
				break;

			case 'NH':
				query = createQueryFromSearchformNhModel(mask.form[mask.searchType]);
				break;

			case 'NF':
				query = createQueryFromSearchformNfModel(mask.form[mask.searchType]);
				break;
		}

		const topsFlopsResult: Array<string> = this._searchTopsFlops(query.topsFlops);

		if (isArray(query.topsFlops) && query.topsFlops.length) {
			if (topsFlopsResult.length === 0) {
				return Observable.of({
					count: 0,
					data: [],
					dayPriceMatrix: null
				});
			}

			query.tags = query.tags.split(',').concat(
				topsFlopsResult.map(hotelId => `GT01-${hotelId}:BT01-${hotelId}`).join(':')
			).join(',');
		}

		if (query.tags.indexOf('TOP-') === -1) {
			let availableTourOperators: Array<string> = [];

			this.tourOperatorCodes$.take(1).subscribe(codes => availableTourOperators = codes);

			if (isArray(availableTourOperators) && availableTourOperators.length) {
				if (isString(query.multiRoomOccupancy) && query.multiRoomOccupancy.length > 0) {
					availableTourOperators = availableTourOperators.filter(top => MULTI_ROOM_UNSUITABLE_TOUR_OPERATORS.indexOf(top) === -1);
				}

				query.tags = query.tags.split(',').concat(availableTourOperators.join(':')).join(',');
			}
		}

		return this._post<GroupsResponseModel>(`${this.baseUrl}/groups`, query.toPostParams())
			.map((res: GroupsResponseModel) => WebserviceService._groupsStringDateToDate(res))
			.map((res: GroupsResponseModel) => WebserviceService._groupsOperatorsObjToArray(res))
			.map((res: GroupsResponseModel) => WebserviceService._transformTagsToMap(res))
			.map((res: GroupsResponseModel) => WebserviceService._extractImportantAttributes(res));
	}
}



// WEBPACK FOOTER //
// ./src/app/xena/core/shared/services/webservice/webservice.service.ts